import { ExternalLink, ImageIcon, Trash2, Upload } from 'lucide-react';
import React from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { toast } from 'sonner';

import { LeadIntegrationsPanel } from '@/components/leads/LeadIntegrationsPanel';
import { AddAttorneyDialog } from '@/components/matter/AddAttorneyDialog';
import { DeleteAttorneyDialog } from '@/components/matter/DeleteAttorneyDialog';
import { EditAttorneyDialog } from '@/components/matter/EditAttorneyDialog';
import { RuleManagement } from '@/components/proofreading/pages/RuleManagement';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Attorney,
  useDeleteAttorneySignatureMutation,
  useListAttorneysQuery,
  useUploadAttorneySignatureMutation,
} from '@/services/api/attorneysService';
import {
  useGetFirmQuery,
  useUpdateFirmMutation,
} from '@/services/api/firmsService';
import { Firm } from '@/services/types/firms-types';

import { FirmClientPortalSettings } from '../client-portal';
import { CmsFeatureConfigManager } from '../cms-features';
import { ErrorAlert } from '../common/ErrorAlert';
import { FirmDemandTemplate } from '../demands/export/firm-demand-template';
import { FirmSidebar } from '../details';
import { BlobSinkConfigManager } from '../document-sync';
import { FilevineOrganizationManager } from '../filevine/FilevineOrganizationManager';
import { PrintableFilevineSections } from '../filevine/PrintableFilevineSections';
import { FirmContainer } from '../FirmContainer';
import { IntakePreferences } from '../intake-preferences';
import { FirmSopPage } from '../sop/FirmSopPage';
import { IntegrationsPage } from '../integrations';
import { OnePasswordList } from '../onepassword/OnePasswordList';
import { FirmRoleAssignments } from '../role-assignments';
import { FirmServicesWorkflowsConfig } from '../services/workflows';
import { TaskLinkingRuleEditor, TaskLinkingRulesPage } from '../task-linking';
import { FirmTemplatesPage } from '../templates';
import { FirmDashboard } from './FirmDashboard';

// Reusable loading skeleton for consistent loading states
const LoadingSkeleton = () => (
  <div className="space-y-4">
    <Skeleton className="h-8 w-1/4" />
    <Skeleton className="h-16 w-full" />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-2">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  </div>
);

const formatAttorneyAddress = (
  address:
    | {
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        state?: string | null;
        postcode?: string | null;
      }
    | null
    | undefined,
): string => {
  if (!address) return '';
  const parts = [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postcode,
  ].filter(Boolean);
  return parts.join(', ');
};

const AttorneySignatureManager = ({
  firmId,
  attorney,
}: {
  firmId: string;
  attorney: Attorney;
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadSignature, { isLoading: isUploading }] =
    useUploadAttorneySignatureMutation();
  const [deleteSignature, { isLoading: isDeleting }] =
    useDeleteAttorneySignatureMutation();
  const isLoading = isUploading || isDeleting;

  const handleUpload = async (file: File | null) => {
    if (!file) return;
    try {
      await uploadSignature({
        firm_id: firmId,
        id: attorney.id,
        file,
      }).unwrap();
      toast.success('Attorney signature updated');
    } catch {
      toast.error('Failed to update attorney signature');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSignature({
        firm_id: firmId,
        id: attorney.id,
      }).unwrap();
      toast.success('Attorney signature removed');
    } catch {
      toast.error('Failed to remove attorney signature');
    }
  };

  return (
    <div className="flex items-center gap-3">
      {attorney.signature ? (
        <button
          type="button"
          onClick={() => window.open(attorney.signature as string, '_blank')}
          className="group/signature relative"
          title="Open signature"
          disabled={isLoading}
        >
          <img
            src={attorney.signature}
            alt={`${attorney.first_name} ${attorney.last_name} signature`}
            className="h-10 w-20 rounded border border-zinc-300 bg-white object-contain p-1"
          />
          <div className="pointer-events-none absolute inset-0 hidden items-center justify-center rounded bg-black/40 text-white group-hover/signature:flex">
            <ExternalLink className="h-3.5 w-3.5" />
          </div>
        </button>
      ) : (
        <div className="flex h-10 w-20 items-center justify-center rounded border border-dashed border-zinc-300 text-xs text-zinc-500">
          <ImageIcon className="mr-1 h-3 w-3" />
          None
        </div>
      )}

      <div className="flex items-center gap-1">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          disabled={isLoading}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            if (file) {
              handleUpload(file).catch(() => undefined);
            }
            event.currentTarget.value = '';
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-2 text-xs"
          disabled={isLoading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mr-1 h-3 w-3" />
          {attorney.signature ? 'Replace' : 'Upload'}
        </Button>

        {attorney.signature && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={handleDelete}
            disabled={isLoading}
            title="Remove signature"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

const FirmAttorneys = ({ firmId }: { firmId: string }) => {
  const {
    data: attorneys = [],
    refetch,
    isLoading,
  } = useListAttorneysQuery({ firm_id: firmId }, { skip: !firmId });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Attorneys</h2>
        <AddAttorneyDialog firmId={firmId} onCreated={() => refetch()} />
      </div>
      <div className="border rounded-md overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Phone</th>
              <th className="px-3 py-2 text-left">Bar/Juris Number</th>
              <th className="px-3 py-2 text-left">Address</th>
              <th className="px-3 py-2 text-left">Signature</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-3 py-3" colSpan={7}>
                  Loading...
                </td>
              </tr>
            ) : attorneys.length === 0 ? (
              <tr>
                <td className="px-3 py-3" colSpan={7}>
                  No attorneys yet
                </td>
              </tr>
            ) : (
              attorneys.map((a) => (
                <tr key={a.id} className="border-t">
                  <td className="px-3 py-2">
                    {a.first_name} {a.last_name}
                  </td>
                  <td className="px-3 py-2">{a.email}</td>
                  <td className="px-3 py-2">{a.phone}</td>
                  <td className="px-3 py-2">
                    {a.bar_number || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {formatAttorneyAddress(a.address) || (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <AttorneySignatureManager firmId={firmId} attorney={a} />
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    <EditAttorneyDialog
                      firmId={firmId}
                      attorney={a}
                      onUpdated={() => refetch()}
                    />
                    <DeleteAttorneyDialog
                      firmId={firmId}
                      attorney={a}
                      onDeleted={() => refetch()}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const FirmDetailsPage: React.FC = () => {
  const { firm_id = '' } = useParams<{ firm_id?: string }>();
  const location = useLocation();

  // Get firm data using RTK Query
  const {
    data: firm,
    isLoading,
    error,
    refetch,
  } = useGetFirmQuery(firm_id, {
    skip: !firm_id,
  });

  // Get update mutation - use the custom API mutation which shares cache tags with useGetFirmQuery
  const [updateFirm, { isLoading: isUpdating }] = useUpdateFirmMutation();

  /**
   * Handles the form submission with updated firm data
   * Makes the API call to update the firm and handles success/error states
   */
  const handleSaveSuccess = async (data: Partial<Firm>) => {
    try {
      if (!firm?.id) {
        throw new Error('Firm ID is required for updates');
      }

      // Use custom API format: { id, ...updates }
      const { id: _id, ...updates } = data as Partial<Firm> & { id?: string };
      await updateFirm({ id: firm.id, ...updates }).unwrap();
      toast.success('Firm updated successfully');
    } catch (err) {
      console.error('Error updating firm:', err);
      toast.error('Failed to update firm');
    }
  };

  // Check if we're on the demand config or proofreading path
  const isOnDemandConfig = location.pathname.includes(
    `/firms/${firm_id}/demand-configurations`,
  );

  const isOnProofreading = location.pathname.includes(
    `/firms/${firm_id}/proofreading`,
  );

  const isOnTemplates =
    location.pathname.includes(`/firms/${firm_id}/templates`) ||
    location.pathname.includes(`/firms/${firm_id}/hipaa-cover-sheet`);

  const isOnAttorneys = location.pathname.includes(
    `/firms/${firm_id}/attorneys`,
  );

  const isOnRoleAssignments = location.pathname.includes(
    `/firms/${firm_id}/role-assignments`,
  );

  const isOnIntakePreferences = location.pathname.includes(
    `/firms/${firm_id}/intake-preferences`,
  );

  const isOnOnePassword = location.pathname.includes(
    `/firms/${firm_id}/onepassword`,
  );

  const isOnFilevineSections = location.pathname.includes(
    `/firms/${firm_id}/filevine-sections`,
  );

  const isOnFilevineOrganization = location.pathname.includes(
    `/firms/${firm_id}/filevine-organization`,
  );

  const isOnServices = location.pathname.includes(`/firms/${firm_id}/services`);

  const isOnLeads = location.pathname.includes(`/firms/${firm_id}/leads`);

  const isOnIntegrations =
    location.pathname.includes(`/firms/${firm_id}/integrations`) ||
    location.pathname.includes(`/firms/${firm_id}/google-drive`);

  const isOnDocumentSync = location.pathname.includes(
    `/firms/${firm_id}/document-sync`,
  );

  const isOnCmsFeatures = location.pathname.includes(
    `/firms/${firm_id}/cms-features`,
  );

  const isOnTaskLinking = location.pathname.includes(
    `/firms/${firm_id}/task-linking`,
  );

  const isOnClientPortal = location.pathname.includes(
    `/firms/${firm_id}/client-portal`,
  );

  const isOnSop = location.pathname.includes(`/firms/${firm_id}/sop`);

  const isOnDetails = location.pathname.includes(
    `/firms/${firm_id}/details`,
  );

  // Show loading state
  if (isLoading || isUpdating) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error || !firm) {
    return (
      <ErrorAlert
        title="Unable to load firm"
        message={error ? error.toString() : 'Firm not found'}
        onRetry={firm_id ? refetch : undefined}
        retryLabel="Retry loading"
      />
    );
  }

  return (
    <div className="flex rounded-lg border border-gray-200 bg-white overflow-hidden" style={{ minHeight: 'calc(100vh - 10rem)' }}>
      <FirmSidebar firmId={firm_id} />

      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {isOnDemandConfig ? (
            <Routes>
              <Route path="demand-configurations">
                <Route
                  index
                  element={<FirmDemandTemplate firmId={firm_id} />}
                />
              </Route>
            </Routes>
          ) : isOnProofreading ? (
            <Routes>
              <Route path="proofreading">
                <Route index element={<RuleManagement firmId={firm_id} />} />
              </Route>
            </Routes>
          ) : isOnTemplates ? (
            <Routes>
              <Route path="templates">
                <Route index element={<FirmTemplatesPage />} />
              </Route>
              <Route path="hipaa-cover-sheet">
                <Route index element={<FirmTemplatesPage />} />
              </Route>
            </Routes>
          ) : isOnAttorneys ? (
            <FirmAttorneys firmId={firm_id} />
          ) : isOnRoleAssignments ? (
            <FirmRoleAssignments firmId={firm_id} />
          ) : isOnIntakePreferences ? (
            <IntakePreferences firm={firm} />
          ) : isOnOnePassword ? (
            <Routes>
              <Route path="onepassword">
                <Route index element={<OnePasswordList firmId={firm_id} />} />
              </Route>
            </Routes>
          ) : isOnFilevineSections ? (
            <PrintableFilevineSections firmId={firm_id} />
          ) : isOnFilevineOrganization ? (
            <FilevineOrganizationManager firmId={firm_id} />
          ) : isOnServices ? (
            <FirmServicesWorkflowsConfig firmId={firm_id} />
          ) : isOnLeads ? (
            <LeadIntegrationsPanel firmId={firm_id} />
          ) : isOnIntegrations ? (
            <IntegrationsPage firmId={firm_id} />
          ) : isOnDocumentSync ? (
            <BlobSinkConfigManager firmId={firm_id} />
          ) : isOnCmsFeatures ? (
            <CmsFeatureConfigManager firmId={firm_id} />
          ) : isOnTaskLinking ? (
            <Routes>
              <Route path="task-linking">
                <Route
                  index
                  element={<TaskLinkingRulesPage firmId={firm_id} />}
                />
                <Route
                  path="new"
                  element={<TaskLinkingRuleEditor firmId={firm_id} />}
                />
                <Route
                  path=":ruleId"
                  element={<TaskLinkingRuleEditor firmId={firm_id} />}
                />
              </Route>
            </Routes>
          ) : isOnClientPortal ? (
            <FirmClientPortalSettings firm={firm} />
          ) : isOnSop ? (
            <FirmSopPage />
          ) : isOnDetails ? (
            <FirmContainer
              firm={firm}
              onSubmit={handleSaveSuccess}
              inline
              isSubmitting={isUpdating}
            />
          ) : (
            <Routes>
              <Route
                index
                element={<FirmDashboard firm={firm} />}
              />
              <Route
                path="*"
                element={<Navigate to={`/firms/${firm_id}`} replace />}
              />
            </Routes>
          )}
        </div>
      </div>
    </div>
  );
};
